<script lang="ts">
  import BaseEditor from './BaseEditor.svelte'
  import { DebouncedCallback } from '$lib/utils/debounce'
  
  interface Props {
    value: string
    placeholder?: string
    readonly?: boolean
    disabled?: boolean
    maxLength?: number
    minHeight?: number
    maxHeight?: number
    debounceDelay?: number
    class?: string
    enableMarkdown?: boolean
    textareaElement?: HTMLTextAreaElement | null
    onInput?: (value: string) => void
    onDebouncedInput?: (value: string) => void
    onClick?: () => void
    onBackspaceAtStart?: () => void
  }
  
  let {
    value = $bindable(),
    placeholder = '검사할 텍스트를 입력하세요...',
    readonly = false,
    disabled = false,
    maxLength,
    minHeight,
    maxHeight,
    debounceDelay = 500,
    class: className = '',
    enableMarkdown = true,
    textareaElement = $bindable(null),
    onInput,
    onDebouncedInput,
    onClick,
    onBackspaceAtStart
  }: Props = $props()
  
  let baseEditor: BaseEditor | null = null
  
  /**
   * TextAreaEditor: Standard textarea implementation
   * Single Responsibility: Handle textarea-specific behavior
   */
  
  // Sync textarea element with parent
  $effect(() => {
    if (baseEditor) {
      const element = baseEditor.getElement()
      if (element && element !== textareaElement) {
        textareaElement = element
      }
    }
  })
  
  // Create debounced callback manager
  const debouncedCallback = onDebouncedInput 
    ? new DebouncedCallback(onDebouncedInput, debounceDelay)
    : null
  
  function handleInput() {
    // Apply markdown auto-formatting if enabled
    if (enableMarkdown) {
      applyMarkdownFormatting()
    }
    
    // Call onInput for value sync
    onInput?.(value)
    
    // Trigger debounced callback for expensive operations
    debouncedCallback?.trigger(value)
  }
  
  function applyMarkdownFormatting() {
    const textarea = baseEditor?.getElement()
    if (!textarea) return
    
    const cursorPos = textarea.selectionStart
    const beforeCursor = value.substring(0, cursorPos)
    const lines = beforeCursor.split('\n')
    const currentLineIndex = lines.length - 1
    const currentLine = lines[currentLineIndex]
    
    // Only process if we just typed a space after - or *
    const trimmed = currentLine.trimStart()
    const indent = currentLine.length - trimmed.length
    
    // Check if we just completed a markdown pattern
    if (trimmed === '- ' || trimmed === '* ') {
      // Convert to bullet point with indentation
      const listIndent = indent > 0 ? indent : 2 // Default 2 spaces if no existing indent
      const newLine = ' '.repeat(listIndent) + '• '
      lines[currentLineIndex] = newLine
      
      // Rebuild the full text
      const afterCursor = value.substring(cursorPos)
      value = lines.join('\n') + afterCursor
      
      // Move cursor to end of bullet (after the space)
      // Use requestAnimationFrame instead of setTimeout for better timing
      requestAnimationFrame(() => {
        if (textarea && document.activeElement === textarea) {
          const newPos = lines.join('\n').length
          textarea.selectionStart = newPos
          textarea.selectionEnd = newPos
        }
      })
    }
    // Check for numbered list
    else if (/^\d+\.\s$/.test(trimmed)) {
      // Add indentation for numbered list
      const listIndent = indent > 0 ? indent : 2 // Default 2 spaces if no existing indent
      const newLine = ' '.repeat(listIndent) + trimmed
      lines[currentLineIndex] = newLine
      
      // Rebuild the full text
      const afterCursor = value.substring(cursorPos)
      value = lines.join('\n') + afterCursor
      
      // Move cursor to end of number list (after the space)
      requestAnimationFrame(() => {
        if (textarea && document.activeElement === textarea) {
          const newPos = lines.join('\n').length
          textarea.selectionStart = newPos
          textarea.selectionEnd = newPos
        }
      })
    }
  }
  
  function handleKeyDown(event: KeyboardEvent) {
    const textarea = baseEditor?.getElement()
    if (!textarea) return
    
    // Don't handle special keys during IME composition
    if (textarea.isComposing || (event as any).isComposing) {
      return
    }
    
    // Handle Backspace at the start of textarea
    if (event.key === 'Backspace' && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
      const selectionStart = textarea.selectionStart
      const selectionEnd = textarea.selectionEnd
      
      // If cursor is at the very beginning and not selecting text
      if (selectionStart === 0 && selectionEnd === 0) {
        event.preventDefault()
        onBackspaceAtStart?.()
        return
      }
    }
    
    // Handle Tab key for indentation (works regardless of markdown setting)
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault()
      
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const spaces = '    ' // Use 4 spaces for indentation
      
      if (start === end) {
        // No selection - check if we're on a list item
        const lines = value.split('\n')
        let currentPos = 0
        let isListItem = false
        let lineIndex = -1
        
        // Find current line
        for (let i = 0; i < lines.length; i++) {
          const lineLength = lines[i].length
          const lineStart = currentPos
          const lineEnd = currentPos + lineLength
          
          if (start >= lineStart && start <= lineEnd) {
            lineIndex = i
            const line = lines[i]
            const trimmed = line.trimStart()
            
            // Check if it's a list item (bullet, dash, or numbered)
            if (trimmed.startsWith('• ') || trimmed.startsWith('- ') || /^(\d+)\.\s/.test(trimmed)) {
              isListItem = true
              
              // Indent the entire line
              lines[i] = spaces + lines[i]
              value = lines.join('\n')
              
              // Move cursor to maintain relative position
              requestAnimationFrame(() => {
                if (textarea && document.activeElement === textarea) {
                  textarea.selectionStart = start + spaces.length
                  textarea.selectionEnd = start + spaces.length
                }
              })
              
              onInput?.(value)
              debouncedCallback?.trigger(value)
              return
            }
            break
          }
          currentPos += lineLength + 1
        }
        
        // If not a list item, insert spaces normally
        if (!isListItem) {
          const before = value.substring(0, start)
          const after = value.substring(start)
          value = before + spaces + after
          
          // Move cursor after the inserted spaces
          requestAnimationFrame(() => {
            if (textarea && document.activeElement === textarea) {
              textarea.selectionStart = start + spaces.length
              textarea.selectionEnd = start + spaces.length
            }
          })
        }
      } else {
        // Text is selected - indent all selected lines
        const lines = value.split('\n')
        let currentPos = 0
        let selectionStartLine = -1
        let selectionEndLine = -1
        
        // Find which lines are selected
        for (let i = 0; i < lines.length; i++) {
          const lineStart = currentPos
          const lineEnd = currentPos + lines[i].length
          
          if (selectionStartLine === -1 && start <= lineEnd) {
            selectionStartLine = i
          }
          if (end <= lineEnd) {
            selectionEndLine = i
            break
          }
          
          currentPos = lineEnd + 1 // +1 for newline
        }
        
        // Indent the selected lines
        let addedLength = 0
        for (let i = selectionStartLine; i <= selectionEndLine; i++) {
          lines[i] = spaces + lines[i]
          addedLength += spaces.length
        }
        
        value = lines.join('\n')
        
        // Adjust selection to maintain it after indentation
        requestAnimationFrame(() => {
          if (textarea && document.activeElement === textarea) {
            textarea.selectionStart = start + spaces.length
            textarea.selectionEnd = end + addedLength
          }
        })
      }
      
      onInput?.(value)
      debouncedCallback?.trigger(value)
      return
    }
    
    // Handle Shift+Tab for outdent (remove indentation)
    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault()
      
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const spaces = '    ' // Match the indent size (4 spaces)
      
      if (start === end) {
        // No selection - find current line
        const lines = value.split('\n')
        let currentPos = 0
        
        for (let i = 0; i < lines.length; i++) {
          const lineLength = lines[i].length
          const lineStart = currentPos
          const lineEnd = currentPos + lineLength
          
          if (start >= lineStart && start <= lineEnd) {
            const line = lines[i]
            
            // Check if line starts with spaces (for any content including lists)
            if (line.startsWith(spaces)) {
              // Remove the leading spaces
              lines[i] = line.substring(spaces.length)
              value = lines.join('\n')
              
              // Move cursor back appropriately
              requestAnimationFrame(() => {
                if (textarea && document.activeElement === textarea) {
                  const newPos = Math.max(lineStart, start - spaces.length)
                  textarea.selectionStart = newPos
                  textarea.selectionEnd = newPos
                }
              })
              
              onInput?.(value)
              debouncedCallback?.trigger(value)
              return
            }
            break
          }
          currentPos += lineLength + 1
        }
      } else {
        // Text is selected - outdent all selected lines
        const lines = value.split('\n')
        let currentPos = 0
        let selectionStartLine = -1
        let selectionEndLine = -1
        
        // Find which lines are selected
        for (let i = 0; i < lines.length; i++) {
          const lineStart = currentPos
          const lineEnd = currentPos + lines[i].length
          
          if (selectionStartLine === -1 && start <= lineEnd) {
            selectionStartLine = i
          }
          if (end <= lineEnd) {
            selectionEndLine = i
            break
          }
          
          currentPos = lineEnd + 1 // +1 for newline
        }
        
        // Outdent the selected lines
        let removedLength = 0
        for (let i = selectionStartLine; i <= selectionEndLine; i++) {
          if (lines[i].startsWith(spaces)) {
            lines[i] = lines[i].substring(spaces.length)
            removedLength += spaces.length
          }
        }
        
        value = lines.join('\n')
        
        // Adjust selection after outdent
        requestAnimationFrame(() => {
          if (textarea && document.activeElement === textarea) {
            const newStart = Math.max(0, start - (selectionStartLine >= 0 && lines[selectionStartLine].startsWith(spaces) ? 0 : spaces.length))
            const newEnd = Math.max(newStart, end - removedLength)
            textarea.selectionStart = newStart
            textarea.selectionEnd = newEnd
          }
        })
      }
      
      onInput?.(value)
      debouncedCallback?.trigger(value)
      return
    }
    
    if (!enableMarkdown) return
    
    // Handle Enter key for list continuation
    if (event.key === 'Enter') {
      const cursorPos = textarea.selectionStart
      const lines = value.split('\n')
      let currentPos = 0
      
      // Find current line
      for (let i = 0; i < lines.length; i++) {
        const lineLength = lines[i].length
        const lineStart = currentPos
        const lineEnd = currentPos + lineLength
        
        if (cursorPos >= lineStart && cursorPos <= lineEnd) {
          const line = lines[i]
          const trimmed = line.trimStart()
          const indent = line.length - trimmed.length
          
          // Check if current line is a list item (bullet or dash)
          if (trimmed.startsWith('• ') || trimmed.startsWith('- ')) {
            event.preventDefault()
            const bullet = trimmed.startsWith('• ') ? '• ' : '- '
            
            // If line only has bullet/dash, end list
            if (trimmed === bullet || trimmed === bullet.trim()) {
              // Remove the empty list item
              lines[i] = ' '.repeat(indent) // Keep indentation but remove bullet
              value = lines.join('\n')
              
              // If indented, might want to go back to parent level
              if (indent > 0) {
                lines[i] = ''
                value = lines.join('\n')
              }
            } else {
              // Continue list on new line with same indentation
              const before = value.substring(0, cursorPos)
              const after = value.substring(cursorPos)
              value = before + '\n' + ' '.repeat(indent) + bullet + after
              
              requestAnimationFrame(() => {
                if (textarea && document.activeElement === textarea) {
                  const newPos = before.length + 1 + indent + bullet.length // position after newline + indent + bullet
                  textarea.selectionStart = newPos
                  textarea.selectionEnd = newPos
                }
              })
            }
            onInput?.(value)
            debouncedCallback?.trigger(value)
            return
          }
          // Numbered list continuation
          else if (/^(\d+)\.\s/.test(trimmed)) {
            event.preventDefault()
            const match = trimmed.match(/^(\d+)\.\s(.*)/)
            if (match && match[2] === '') {
              // Empty numbered item, end list
              if (indent > 0) {
                lines[i] = ''
              } else {
                lines[i] = ''
              }
              value = lines.join('\n')
            } else if (match) {
              // Continue with next number, preserving indentation
              const nextNum = parseInt(match[1]) + 1
              const before = value.substring(0, cursorPos)
              const after = value.substring(cursorPos)
              value = before + '\n' + ' '.repeat(indent) + nextNum + '. ' + after
              
              requestAnimationFrame(() => {
                if (textarea && document.activeElement === textarea) {
                  const newPos = before.length + 1 + indent + nextNum.toString().length + 2 // position after newline + indent + "N. "
                  textarea.selectionStart = newPos
                  textarea.selectionEnd = newPos
                }
              })
            }
            onInput?.(value)
            debouncedCallback?.trigger(value)
            return
          }
          break
        }
        
        currentPos += lineLength + 1
      }
    }
  }
  
  // Clean up on destroy
  $effect(() => {
    return () => {
      debouncedCallback?.destroy()
    }
  })
  
  export function focus() {
    baseEditor?.focus()
  }
  
  export function blur() {
    baseEditor?.blur()
  }
  
  export function select() {
    baseEditor?.select()
  }
  
  // Get character count class based on length
  function getCharCountClass(length: number): string {
    if (length > 5000) return 'text-red'
    if (length > 2000) return 'text-yellow'
    return 'text-gray'
  }
</script>

<div class="textarea-editor-container">
  <BaseEditor
    bind:this={baseEditor}
    bind:value
    placeholder=""
    {readonly}
    {disabled}
    {maxLength}
    {minHeight}
    {maxHeight}
    class="textarea-editor {className} editor-no-scroll"
    aria-label="문서 본문"
    oninput={handleInput}
    onkeydown={handleKeyDown}
    onclick={onClick}
  />
  
  <!-- Character counter always visible -->
  <div class="char-counter">
    <span class="char-count {getCharCountClass(value?.length || 0)}">
      {(value?.length || 0).toLocaleString()}자
    </span>
  </div>
</div>

<style>
  .textarea-editor-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  /* Let BaseEditor handle its own scrolling in standalone mode */
  .textarea-editor-container :global(.base-editor-wrapper) {
    flex: 1;
    width: 100%;
  }
  
  :global(.textarea-editor) {
    flex: 1;
  }
  
  .char-counter {
    position: fixed;
    bottom: 10px;
    right: 12px;
    font-size: var(--ui-font-size-sm);
    font-family: 'SF Mono', Monaco, 'Courier New', monospace;
    pointer-events: none;
    user-select: none;
    opacity: 0.9;
    contain: layout;
    will-change: transform;
    z-index: 10;
    transform: translateZ(0);
    backface-visibility: hidden;
  }
  
  /* Protect char counter during sidebar transitions */
  :global(.sidebar-transitioning) .char-counter {
    transition: none !important;
  }
  
  .char-count {
    font-weight: 500;
    transition: color 0.2s ease;
  }
  
  .char-count.text-gray {
    color: var(--text-secondary);
  }
  
  .char-count.text-yellow {
    color: var(--warning);
  }
  
  .char-count.text-red {
    color: var(--error);
  }
  
  /* Dark mode handled by CSS variables */
</style>
